
import {
  defineComponent,
  ref,
  computed,
  onMounted,
  onUnmounted,
  watch
} from "vue";
import { useI18n } from "vue-i18n";
import TableLeaderboard from "./components/table-leaderboard.vue";
import CustomerLeaderboardTagFilter from "@/app/ui/components/customer-leaderboard-tag-filter/index.vue";
import { RevenueDashboardController } from "@/app/ui/controllers/RevenueDashboardController";
import { AccountController } from "@/app/ui/controllers/AccountController";
import {
  RevenueDashboardCustomerListRequest,
  RevenueDashboardCustomerState
} from "@/domain/entities/RevenueDashboard";
import dateToDateString from "@/app/infrastructures/misc/common-library/DateToDateString";

export default defineComponent({
  name: "TabCustomer",
  components: {
    TableLeaderboard,
    CustomerLeaderboardTagFilter
  },
  setup() {
    const { t } = useI18n();
    const stateData = ref(new RevenueDashboardCustomerState());
    const currentPage = ref(1);
    const limit = 20;
    const hasMore = ref(true);
    const opTooltip = ref<any>(null);
    const lastUpdate = ref("");
    const sortBy = ref<string>("");
    const orderBy = ref<string>("");
    const searchQuery = computed(
      () => RevenueDashboardController.customerLeaderboardSearchQuery
    );
    const activeTags = computed(
      () => RevenueDashboardController.activeCustomerLeaderboardTags
    );
    const accountCurrency = computed(
      () =>
        (AccountController.accountData?.accountCurrency as "IDR" | "MYR") ??
        "IDR"
    );
    const monthKeys = [
      "january",
      "february",
      "march",
      "april",
      "may",
      "june",
      "july",
      "august",
      "september",
      "october",
      "november",
      "december"
    ];
    const customerTitle = computed(() => {
      const date = new Date();
      const month = t(
        `dashboard.customer.months.${monthKeys[date.getMonth()]}`
      );
      return `${t("dashboard.customer.topCustomer")} ${month}`;
    });
    const lastUpdateAt = computed(() => {
      const date = lastUpdate.value
        ? dateToDateString(lastUpdate.value, true, true)
        : "-";
      return `${t("dashboard.customer.updated")} ${date}`;
    });

    const showTooltip = (event: MouseEvent) => {
      opTooltip.value.show(event);

      requestAnimationFrame(() => {
        const panel = opTooltip.value.container;
        const icon = event.target as HTMLElement;
        if (!panel) return;

        const iconRect = icon.getBoundingClientRect();
        const panelRect = panel.getBoundingClientRect();

        panel.style.top = `${iconRect.top - panelRect.height - 12}px`;
        panel.style.left = `${iconRect.left +
          iconRect.width / 2 -
          panelRect.width / 2}px`;
      });
    };

    const hideTooltip = () => {
      opTooltip.value.hide();
    };

    const getCustomerLeaderboard = async (page = 1, append = false) => {
      try {
        if (page === 1) {
          stateData.value.isLoadingCustomerLeaderboard = true;
        }
        const resp = await RevenueDashboardController.getCustomerLeaderboard(
          new RevenueDashboardCustomerListRequest({
            page: currentPage.value,
            limit,
            customerName: searchQuery.value,
            tags: activeTags.value
              .map((tag) => {
                const mapping: Record<string, string> = {
                  // eslint-disable-next-line @typescript-eslint/camelcase
                  omzet_naik: "increased_revenue",
                  // eslint-disable-next-line @typescript-eslint/camelcase
                  omzet_turun: "decreased_revenue",
                  // eslint-disable-next-line @typescript-eslint/camelcase
                  pelanggan_baru: "is_new_customer",
                  // eslint-disable-next-line @typescript-eslint/camelcase
                  pelanggan_churn: "is_churned_customer",
                  // eslint-disable-next-line @typescript-eslint/camelcase
                  rts_5: "rts_above_5_percent",
                  // eslint-disable-next-line @typescript-eslint/camelcase
                  missbooking_5: "misbooking_above_5_percent"
                };
                return mapping[tag] || tag;
              })
              .join(","),

            sortBy: sortBy.value,
            orderBy: orderBy.value
          } as any)
        );

        hasMore.value = resp.data.length > 0;
        if (page === 1) {
          lastUpdate.value = resp.lastSyncAt || "";
        }

        if (append) {
          stateData.value.customerLeaderboardData.data.push(...resp.data);
        } else {
          stateData.value.customerLeaderboardData = resp;
        }
      } catch {
        stateData.value.errorCustomerLeaderboard = true;
      } finally {
        stateData.value.isLoadingCustomerLeaderboard = false;
      }
    };

    const loadMoreLeaderboard = () => {
      currentPage.value += 1;
      getCustomerLeaderboard(currentPage.value, true);
    };

    const onSort = (payload: { sortBy: string; orderBy: "asc" | "desc" }) => {
      sortBy.value = payload.sortBy;
      orderBy.value = payload.orderBy;
      currentPage.value = 1;
      getCustomerLeaderboard(1, false);
    };

    const onFilterChanged = () => {
      currentPage.value = 1;
      getCustomerLeaderboard(1, false);
    };

    watch([searchQuery, activeTags], () => {
      currentPage.value = 1;
      getCustomerLeaderboard(1, false);
    });

    onMounted(() => {
      getCustomerLeaderboard();
    });

    // Reset active tags when leaving this page
    onUnmounted(() => {
      RevenueDashboardController.setCustomerLeaderboardTags([]);
      RevenueDashboardController.setCustomerLeaderboardSearch("");
    });

    // ✅ Expose to template
    return {
      stateData,
      loadMoreLeaderboard,
      onSort,
      onFilterChanged,
      hasMore,
      customerTitle,
      opTooltip,
      showTooltip,
      hideTooltip,
      lastUpdateAt,
      accountCurrency
    };
  }
});
